/**
 * AccessControl Component
 *
 * Enforces persona-based access control per PRD requirements.
 * Supports both legacy role-based checks and the new plan-gating system.
 */

import { AlertCircle, ArrowLeft, Lock, MapPin, Smartphone } from '@tonnie/assets';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent } from '@tonnie/ui';
import { ReactNode } from 'react';
import { Link } from 'wouter';
import { useAuth } from '@/core/contexts/AuthContext';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import { AppShellLayout } from '../layout/AppShellLayout';
import {
  getFeatureAccess,
  getUpgradeMessage,
  type FeatureType,
} from '@/features/platform/auth/utils';

interface IAccessControlProps {
  children: ReactNode;
  requireFamilyMaster?: boolean;
  requireTalentOrFamilyMaster?: boolean;
  redirectToCitizen?: boolean;
  citizenFeature?: string;
  viaBilling?: boolean;
  requiresJourney?: boolean;
  requiresFamilyBilling?: boolean;
  /** New: plan-gating feature check (takes precedence when set) */
  requireFeature?: FeatureType;
}

/**
 * AccessControl component enforces persona-based access per PRD requirements.
 *
 * Per PRD §0.4:
 * - Talent: Full access to all features
 * - Family Master: Full access + family management
 * - Family Member: Limited access (base features + read-only access to selected features)
 */
export function AccessControl({
  children,
  requireFamilyMaster = false,
  requireTalentOrFamilyMaster = false,
  redirectToCitizen = false,
  citizenFeature,
  requiresJourney = false,
  requiresFamilyBilling = false,
  requireFeature,
}: IAccessControlProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeInstance } = useJourneyInstance();

  // Role checks — 'talent' is the default for any authenticated user without
  // an explicit family role. The SDK may return 'citizen' from Clerk metadata,
  // which AuthContext now maps to 'talent', but we guard here too just in case.
  const isFamilyMaster = user?.role === 'family_master';
  const isFamilyMember = user?.role === 'family_member';
  const isTalent = !!user && !isFamilyMaster && !isFamilyMember;

  // Check if user has at least one journey instance
  const hasJourney = user?.journeyInstances && user.journeyInstances.length > 0;

  // Check if Family Program is enabled (paid via billing)
  const hasFamilyBilling = user?.familyProgram?.enabled === true;

  // ─── Plan-gating check (new system) ─────────────────────────────────
  if (requireFeature && user) {
    const result = getFeatureAccess(user, requireFeature, activeInstance ?? undefined);
    if (!result.canAccess) {
      const isHeroNotEnabled = result.reason === 'hero_not_enabled';

      const lockedContent = isHeroNotEnabled ? (
        <div
          className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
          data-testid="access-control-locked"
        >
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-tone-amber mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('accessControl.heroNotEnabled')}</h3>
              <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                {t('accessControl.heroNotEnabledDescription')}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" asChild>
                  <Link href="/dashboard">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common.back')}
                  </Link>
                </Button>
                <Button asChild>
                  <Link href="/dashboard">
                    <MapPin className="w-4 h-4 mr-2" />
                    {t('accessControl.switchJourney')}
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div
          className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
          data-testid="access-control-locked"
        >
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6 text-center">
              <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('accessControl.featureLocked')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {getUpgradeMessage(t, requireFeature)}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" asChild>
                  <Link href="/dashboard">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common.back')}
                  </Link>
                </Button>
                {result.upgradeRequired && (
                  <Button asChild>
                    <Link href="/billing">{t('accessControl.upgrade')}</Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );

      return lockedContent;
    }
  }

  // ─── Legacy role-based checks ──────────────────────────────────────

  // Check if user has required access
  let hasAccess: boolean;
  let accessDeniedReason = '';

  if (requireFamilyMaster) {
    hasAccess = !!isFamilyMaster;
    if (!hasAccess) {
      accessDeniedReason = t('accessControl.familyMasterOnly');
    }
  } else if (requireTalentOrFamilyMaster) {
    hasAccess = !!(isTalent || isFamilyMaster);
    if (!hasAccess) {
      accessDeniedReason = t('accessControl.talentOrFamilyMasterOnly');
    }
  } else {
    hasAccess = !!(isTalent || isFamilyMaster || isFamilyMember);
  }

  // Handle Family Member redirect to Citizen for certain features
  if (isFamilyMember && redirectToCitizen) {
    return (
      <AppShellLayout>
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6 text-center">
              <Smartphone className="w-12 h-12 mx-auto text-primary-accessible mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t('accessControl.useCitizenMobileApp')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {citizenFeature
                  ? t('accessControl.accessFeatureInCitizenApp', { feature: citizenFeature })
                  : t('accessControl.featureInCitizenApp')}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" asChild>
                  <Link href="/dashboard">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common.back')}
                  </Link>
                </Button>
                <Button>
                  <Smartphone className="w-4 h-4 mr-2" />
                  {t('accessControl.openCitizen')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppShellLayout>
    );
  }

  // Check if requires Journey but user doesn't have one
  if (requiresJourney && !hasJourney) {
    return (
      <AppShellLayout>
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6 text-center">
              <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('accessControl.journeyRequired')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('accessControl.needJourneyToAccess')}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" asChild>
                  <Link href="/dashboard">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common.back')}
                  </Link>
                </Button>
                <Button asChild>
                  <Link href="/journey/builder?new=1">{t('accessControl.buildJourney')}</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppShellLayout>
    );
  }

  // Check if requires Family Billing but user hasn't paid
  if (requiresFamilyBilling && !hasFamilyBilling) {
    return (
      <AppShellLayout>
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6 text-center">
              <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {t('accessControl.familyProgramRequired')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('accessControl.enableFamilyProgram')}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" asChild>
                  <Link href="/dashboard">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('common.back')}
                  </Link>
                </Button>
                <Button asChild>
                  <Link href="/billing">{t('accessControl.goToBilling')}</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AppShellLayout>
    );
  }

  // Show access denied message
  if (!hasAccess) {
    return (
      <AppShellLayout>
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Card className="border-border/50 shadow-sm">
            <CardContent className="pt-6 text-center">
              <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('accessControl.accessDenied')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {accessDeniedReason || t('accessControl.noPermission')}
              </p>
              <Button variant="outline" asChild>
                <Link href="/dashboard">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('common.back')}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppShellLayout>
    );
  }

  // User has access, render children
  return <>{children}</>;
}
